import { FC, useMemo } from "react";
import { Card, CardContent, Typography, CardMedia, Link } from "@mui/material";

interface ExternalPreviewProps {
  html: string;
}

const ExternalPreview: FC<ExternalPreviewProps> = ({ html }) => {
  const parsed = useMemo(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const anchor = doc.querySelector("a");
    const href = anchor?.getAttribute("href") ?? "";
    const img = doc.querySelector("img");
    const imgSrc = img?.getAttribute("src") ?? "";
    const title = doc.querySelector("div[style*='font-weight']")?.textContent?.trim() ?? "";
    const description =
      doc.querySelector("div[style*='font-size:0.9em']")?.textContent?.trim() ?? "";
    const domain = doc.querySelector("div[style*='font-size:0.8em']")?.textContent?.trim() ?? "";

    return { href, imgSrc, title, description, domain };
  }, [html]);

  if (!parsed.href) return null;

  return (
    <Link
      href={parsed.href}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      color="inherit"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid #ccc",
        }}
      >
        {parsed.imgSrc && (
          <CardMedia
            component="img"
            image={parsed.imgSrc}
            alt={parsed.title}
            sx={{ objectFit: "cover", maxHeight: 250 }}
          />
        )}
        <CardContent sx={{ p: 1.5 }}>
          {parsed.title && (
            <Typography variant="subtitle1" fontWeight={600}>
              {parsed.title}
            </Typography>
          )}
          {parsed.description && (
            <Typography variant="body2" color="text.secondary">
              {parsed.description}
            </Typography>
          )}
          {parsed.domain && (
            <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
              {parsed.domain}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default ExternalPreview;
